import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  query,
  runTransaction,
  where,
} from 'firebase/firestore';
import { db } from '../firebase';

export const TELLER_PRO_PRICE_EUR = 9.9;
export const CLIENT_PRO_PRICE_EUR = 4.9;
export const TELLER_PRO_ORDER_SUFFIX = 'tellerSubscriptionPlan';
export const CLIENT_PRO_ORDER_SUFFIX = 'subscriptionPlan';

const DEFAULT_PAYMENT_SETTINGS = {
  iban: 'SK9611000000002918599669',
  beneficiaryName: 'fate room',
  proPriceEUR: TELLER_PRO_PRICE_EUR,
  clientProPriceEUR: CLIENT_PRO_PRICE_EUR,
};

const nowIso = () => new Date().toISOString();
const variableSymbol = () => String(Date.now()).slice(-10);
const upgradeOrderId = (uid) => `${uid}_${TELLER_PRO_ORDER_SUFFIX}`;
const cancellationOrderId = (uid) => `${uid}_${TELLER_PRO_ORDER_SUFFIX}_cancel`;
const clientUpgradeOrderId = (uid) => `${uid}_${CLIENT_PRO_ORDER_SUFFIX}`;
const clientCancellationOrderId = (uid) => `${uid}_${CLIENT_PRO_ORDER_SUFFIX}_cancel`;

export async function getProPaymentSettings() {
  try {
    const snapshot = await getDoc(doc(db, 'appSettings', 'payments'));
    if (!snapshot.exists()) return DEFAULT_PAYMENT_SETTINGS;
    return { ...DEFAULT_PAYMENT_SETTINGS, ...snapshot.data() };
  } catch {
    return DEFAULT_PAYMENT_SETTINGS;
  }
}

export function subscribeTellerPro(uid, callback, onError) {
  if (!uid) return () => {};

  const state = {
    plan: 'free',
    upgradeOrder: null,
    cancellationOrder: null,
  };
  const emit = () => callback({ ...state });
  const handleError = (error) => onError?.(error);

  const unsubscribers = [
    onSnapshot(doc(db, 'users', uid), (snapshot) => {
      state.plan = snapshot.exists() ? snapshot.data().tellerSubscriptionPlan || 'free' : 'free';
      emit();
    }, handleError),
    onSnapshot(
      query(collection(db, 'proOrders'), where('accountId', '==', uid)),
      (snapshot) => {
        const orders = snapshot.docs.map((orderDoc) => ({ id: orderDoc.id, ...orderDoc.data() }));
        state.upgradeOrder = orders.find((order) => order.requestType !== 'cancel-pro' && order.planField === TELLER_PRO_ORDER_SUFFIX) || null;
        state.cancellationOrder = orders.find((order) => order.requestType === 'cancel-pro' && order.planField === TELLER_PRO_ORDER_SUFFIX) || null;
        emit();
      },
      handleError,
    ),
  ];

  return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
}

export function subscribeClientPro(uid, callback, onError) {
  if (!uid) return () => {};

  const state = {
    plan: 'free',
    upgradeOrder: null,
    cancellationOrder: null,
  };
  const emit = () => callback({ ...state });
  const handleError = (error) => onError?.(error);

  const unsubscribers = [
    onSnapshot(doc(db, 'users', uid), (snapshot) => {
      state.plan = snapshot.exists() ? snapshot.data().subscriptionPlan || 'free' : 'free';
      emit();
    }, handleError),
    onSnapshot(
      query(collection(db, 'proOrders'), where('accountId', '==', uid)),
      (snapshot) => {
        const orders = snapshot.docs.map((orderDoc) => ({ id: orderDoc.id, ...orderDoc.data() }));
        state.upgradeOrder = orders.find((order) => order.requestType !== 'cancel-pro' && order.planField === CLIENT_PRO_ORDER_SUFFIX) || null;
        state.cancellationOrder = orders.find((order) => order.requestType === 'cancel-pro' && order.planField === CLIENT_PRO_ORDER_SUFFIX) || null;
        emit();
      },
      handleError,
    ),
  ];

  return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
}

export async function requestTellerPro(user) {
  if (!user?.uid) throw new Error('Sign in as a reader to activate Pro.');

  const settings = await getProPaymentSettings();
  const amountEUR = Number(settings.proPriceEUR || TELLER_PRO_PRICE_EUR);
  if (!Number.isFinite(amountEUR) || amountEUR <= 0 || amountEUR > 500) {
    throw new Error('The configured Reader Pro price is invalid.');
  }

  const orderRef = doc(db, 'proOrders', upgradeOrderId(user.uid));
  const order = await runTransaction(db, async (transaction) => {
    const snapshot = await transaction.get(orderRef);
    if (snapshot.exists()) {
      const existing = { id: snapshot.id, ...snapshot.data() };
      if (['pending', 'paid', 'approved', 'active'].includes(existing.status)) return existing;
    }

    const timestamp = nowIso();
    const data = {
      accountId: user.uid,
      userId: user.uid,
      tellerId: user.uid,
      role: 'teller',
      planField: TELLER_PRO_ORDER_SUFFIX,
      requestType: 'activate-pro',
      email: String(user.email || '').trim().toLowerCase(),
      amountEUR,
      variableSymbol: variableSymbol(),
      status: 'pending',
      createdAt: timestamp,
      updatedAt: timestamp,
    };
    transaction.set(orderRef, data, { merge: true });
    return { id: orderRef.id, ...data };
  });

  return { order, settings };
}

export async function requestTellerProCancellation(user) {
  if (!user?.uid) throw new Error('Sign in as a reader to manage Pro.');

  const settings = await getProPaymentSettings();
  const amountEUR = Number(settings.proPriceEUR || TELLER_PRO_PRICE_EUR);
  const requestRef = doc(db, 'proOrders', cancellationOrderId(user.uid));

  return runTransaction(db, async (transaction) => {
    const snapshot = await transaction.get(requestRef);
    if (snapshot.exists()) {
      const existing = { id: snapshot.id, ...snapshot.data() };
      if (existing.status === 'pending') return existing;
    }

    const timestamp = nowIso();
    const data = {
      accountId: user.uid,
      userId: user.uid,
      tellerId: user.uid,
      role: 'teller',
      planField: TELLER_PRO_ORDER_SUFFIX,
      requestType: 'cancel-pro',
      targetOrderId: upgradeOrderId(user.uid),
      email: String(user.email || '').trim().toLowerCase(),
      amountEUR,
      variableSymbol: variableSymbol(),
      status: 'pending',
      createdAt: timestamp,
      updatedAt: timestamp,
    };
    transaction.set(requestRef, data, { merge: true });
    return { id: requestRef.id, ...data };
  });
}


export async function requestClientPro(user) {
  if (!user?.uid) throw new Error('Sign in to activate Client Pro.');

  const settings = await getProPaymentSettings();
  const amountEUR = Number(settings.clientProPriceEUR || CLIENT_PRO_PRICE_EUR);
  if (!Number.isFinite(amountEUR) || amountEUR <= 0 || amountEUR > 500) {
    throw new Error('The configured Client Pro price is invalid.');
  }

  const orderRef = doc(db, 'proOrders', clientUpgradeOrderId(user.uid));
  const order = await runTransaction(db, async (transaction) => {
    const snapshot = await transaction.get(orderRef);
    if (snapshot.exists()) {
      const existing = { id: snapshot.id, ...snapshot.data() };
      if (['pending', 'paid', 'approved', 'active'].includes(existing.status)) return existing;
    }

    const timestamp = nowIso();
    const data = {
      accountId: user.uid,
      userId: user.uid,
      role: 'user',
      planField: CLIENT_PRO_ORDER_SUFFIX,
      requestType: 'activate-pro',
      email: String(user.email || '').trim().toLowerCase(),
      amountEUR,
      variableSymbol: variableSymbol(),
      status: 'pending',
      createdAt: timestamp,
      updatedAt: timestamp,
    };
    transaction.set(orderRef, data, { merge: true });
    return { id: orderRef.id, ...data };
  });

  return { order, settings };
}

export async function requestClientProCancellation(user) {
  if (!user?.uid) throw new Error('Sign in to manage Client Pro.');

  const settings = await getProPaymentSettings();
  const amountEUR = Number(settings.clientProPriceEUR || CLIENT_PRO_PRICE_EUR);
  const requestRef = doc(db, 'proOrders', clientCancellationOrderId(user.uid));

  return runTransaction(db, async (transaction) => {
    const snapshot = await transaction.get(requestRef);
    if (snapshot.exists()) {
      const existing = { id: snapshot.id, ...snapshot.data() };
      if (existing.status === 'pending') return existing;
    }

    const timestamp = nowIso();
    const data = {
      accountId: user.uid,
      userId: user.uid,
      role: 'user',
      planField: CLIENT_PRO_ORDER_SUFFIX,
      requestType: 'cancel-pro',
      targetOrderId: clientUpgradeOrderId(user.uid),
      email: String(user.email || '').trim().toLowerCase(),
      amountEUR,
      variableSymbol: variableSymbol(),
      status: 'pending',
      createdAt: timestamp,
      updatedAt: timestamp,
    };
    transaction.set(requestRef, data, { merge: true });
    return { id: requestRef.id, ...data };
  });
}
